import React, { useState, useMemo } from 'react';
import { CommentWithContentContext, ContentItemWithComments } from 'shared/types/contentTypes';
import CommentCard from '../CommentCard/CommentCard';
import ReplyThread from '../ReplyThread/ReplyThread';
import NewCommentForm from '../NewCommentForm';
import CommentFilters, { FilterOption } from '../CommentFilters/CommentFilters';
import { useAuth } from '../../../hooks/useAuth';
// We're using a simplified context header now
// import { getContentContextHeaderText } from '../../../utils/contentHelpers';

interface CommentFeedProps {
  contentItems: ContentItemWithComments[];
  isLoading: boolean;
  onAddComment: (text: string, contentId?: string, mentions?: import('shared/types/contentTypes').MentionMetadata[]) => Promise<void>;
  onReply: (commentId: string, contentId: string, text: string, mentions?: import('shared/types/contentTypes').MentionMetadata[]) => Promise<void>;
  onLike: (commentId: string, isLiked: boolean, contentId: string) => Promise<void>;
  onEdit?: (commentId: string, text: string, contentId: string) => Promise<void>;
  onDelete?: (commentId: string, contentId: string) => Promise<void>;
  onFilter?: (filterId: string) => void;
  activeFilter?: string;
  filterOptions?: FilterOption[];
  className?: string;
  compact?: boolean;
  commentPlaceholder?: string;
  entityName?: string;
  recentCommentTimeWindow?: number; // In minutes
  pageContext?: 'artist' | 'album' | 'track' | 'dashboard' | 'contentCard' | 'community'; // New prop for page context
  currentAlbumId?: string; // Optional: for album page context
  currentEntityId?: string; // Optional: for any entity page context
}

const CommentFeed: React.FC<CommentFeedProps> = ({
  contentItems,
  isLoading,
  onAddComment,
  onReply,
  onLike,
  onEdit,
  onDelete,
  onFilter,
  activeFilter = 'all',
  filterOptions = [],
  className = '',
  compact = false,
  commentPlaceholder,
  entityName = '',
  recentCommentTimeWindow = 30, // Default to 30 minutes
  pageContext,
  currentAlbumId,
  currentEntityId,
}) => {
  const { currentUser2: currentUser } = useAuth();
  // Keep this state for compatibility with the component interface
  // We're not using this anymore since comments are always expanded
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});
  // State to track which comment is in "adding reply" mode
  const [addingReplyToCommentId, setAddingReplyToCommentId] = useState<string | null>(null);
  // State to control visibility of "new thread" form in contentCard context
  const [showNewThreadForm, setShowNewThreadForm] = useState(false);
  // We no longer need expandedContentItems/setExpandedContentItems since we removed the content context headers
  // const [expandedContentItems, setExpandedContentItems] = useState<Record<string, boolean>>({});
  
  // Context-aware logic to determine if content context should be shown
  const shouldShowContentContext = (comment: CommentWithContentContext): boolean => {
    const { contentType, contentId } = comment.contentContext;
    
    // ContentFeedCard context - never show context (we're already viewing that content)
    if (pageContext === 'contentCard') {
      return false;
    }
    
    // Artist Page logic
    if (pageContext === 'artist') {
      // All conversations - always show context (mixed content needs context)
      if (activeFilter === 'all') return true;
      // Artist conversations - never show context (redundant - we're on artist page)
      if (activeFilter === 'direct') return false;
      // Album/Track conversations - always show context (need to know which album/track)
      if (activeFilter === 'album' || activeFilter === 'track') return true;
    }
    
    // Album Page logic (for future implementation)
    if (pageContext === 'album' && currentAlbumId) {
      // Don't show context for comments about this album
      if (contentType === 'musicRelease' && contentId === currentAlbumId) return false;
      // Show context for track comments within the album
      if (contentType === 'musicTrack') return true;
      // Show context for other content types
      return true;
    }
    
    // Community page - always show context (comments come from multiple artists/albums)
    if (pageContext === 'community') {
      return true;
    }

    // Dashboard or other contexts - use default behavior
    // Don't show context for discussion content items
    return contentType !== 'discussion';
  };
  
  // Function to toggle reply visibility
  const toggleReplies = (commentId: string) => {
    // Allow toggling regardless of content to support our new UI
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  // We keep this function for potential future use but remove it from active code
  // since we no longer have content headers to expand/collapse
  /* 
  const toggleContentItem = (contentId: string) => {
    setExpandedContentItems(prev => ({
      ...prev,
      [contentId]: !prev[contentId]
    }));
  };
  */

  // Handler for adding a new comment
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSubmitComment = async (text: string, mentions?: import('shared/types/contentTypes').MentionMetadata[]) => {
    // CRITICAL: If we're viewing a specific content item (contentCard context),
    // ALWAYS add to that item - never create new content or use time window logic
    if (pageContext === 'contentCard' && contentItems.length > 0) {
      const targetContentId = contentItems[0].contentItem.id;
      console.log(`[handleSubmitComment] ContentCard context - adding to existing item: ${targetContentId}`);
      await onAddComment(text, targetContentId, mentions);
      return;
    }

    // Only allow adding new comments for 'all' or 'direct' filters
    if (activeFilter !== 'all' && activeFilter !== 'direct') {
      console.warn('Cannot add comments when filtering by album or track');
      return;
    }

    // Find the most recent artist discussion content item to add the comment to
    const artistDiscussionItems = contentItems.filter(item =>
      item.contentItem.type === 'discussion'
    );
    
    // Helper: Extract timestamp from content item
    const extractItemTimestamp = (item: ContentItemWithComments): number => {
      // Priority 1: Use most recent comment timestamp if comments exist
      if (item.comments.length > 0) {
        return Math.max(...item.comments.map(c => new Date(c.createdAt).getTime()));
      }

      // Priority 2: Use content item's createdAt (required field per ContentItem type)
      const originalItem = contentItems.find(ci => ci.contentItem.id === item.contentItem.id);
      const contentItem = originalItem?.contentItem || item.contentItem;

      // ContentItem.createdAt is a required field, but handle missing data gracefully
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const createdAt = (contentItem as any).createdAt;
      if (createdAt && typeof createdAt === 'string') {
        const timestamp = new Date(createdAt).getTime();
        if (!isNaN(timestamp)) {
          return timestamp;
        }
      }

      // Fallback: treat missing/invalid timestamps as very old
      console.warn(`[CommentFeed] Invalid or missing createdAt for content item ${item.contentItem.id}`);
      return 0;
    };

    if (artistDiscussionItems.length > 0) {
      // Sort by creation date (newest first)
      const sortedItems = [...artistDiscussionItems].sort((a, b) => {
        return extractItemTimestamp(b) - extractItemTimestamp(a);
      });

      const mostRecentItem = sortedItems[0];

      const mostRecentTime = extractItemTimestamp(mostRecentItem);
      const timeWindow = recentCommentTimeWindow * 60 * 1000; // Convert minutes to milliseconds
      const isWithinTimeWindow = Date.now() - mostRecentTime < timeWindow;
      
      // Log details for debugging
      console.log(`[handleSubmitComment] Most recent item time: ${new Date(mostRecentTime).toISOString()}, ` +
                 `Current time: ${new Date().toISOString()}, ` +
                 `Time window: ${recentCommentTimeWindow} minutes, ` +
                 `Is within window: ${isWithinTimeWindow}`);
      
      if (isWithinTimeWindow) {
        // If within time window, use the most recent discussion item
        console.log(`[handleSubmitComment] Adding to existing content item: ${mostRecentItem.contentItem.id}`);
        await onAddComment(text, mostRecentItem.contentItem.id, mentions);
      } else {
        // If not within time window, create a new content item
        console.log(`[handleSubmitComment] Creating new content item for comment`);
        await onAddComment(text, "", mentions);
      }
    } else {
      // No existing artist discussion items, create a new one
      console.log(`[handleSubmitComment] No discussion items found - creating new content item`);
      await onAddComment(text, "", mentions);
    }
  };
  
  // Handler for starting to add a reply
  const handleStartAddingReply = (commentId: string) => {
    setAddingReplyToCommentId(commentId);
    // Auto-expand the comment thread when starting to add a reply
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: true
    }));
  };
  
  // Handler for canceling adding a reply
  const handleCancelAddingReply = () => {
    setAddingReplyToCommentId(null);
  };
  
  // Handler for replying to a comment
  const handleReply = async (commentId: string, contentId: string, text: string, mentions?: import('shared/types/contentTypes').MentionMetadata[]): Promise<void> => {
    await onReply(commentId, contentId, text, mentions);

    // Auto-expand the comment thread that was replied to
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: true
    }));

    // Return void instead of null (CommentReplies expects Promise<void>)
  };
  
  // Get the title based on filter and count
  const getTitle = () => {
    return null
    // const filterText = filterOptions.find(opt => opt.id === activeFilter)?.label || 'Conversation';
    // return compact ? filterText : `${filterText}`;
  };
  
  // Get all comments from all content items and sort them chronologically
  const getAllCommentsChronologically = useMemo(() => {
    // Extract all comments from all content items
    const allComments = contentItems.flatMap(item => 
      item.comments
        // Only include top-level comments (no parentCommentId)
        .filter(comment => !comment.parentCommentId)
        // Adding a reference to the content item for context
        .map(comment => ({
          ...comment,
          contentItem: item.contentItem
        }))
    );
    
    // Sort by creation date (newest first)
    return allComments.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [contentItems]);
  
  // We use this value directly in getTitle() instead of storing it separately
  // const totalCommentCount = useMemo(() => {
  //   return contentItems.reduce((sum, item) => sum + item.comments.length, 0);
  // }, [contentItems]);
  
  // Helper to find replies to a comment - this finds only replies that were loaded with the parent comments
  // Note: This will be supplemented by the useCommentReplies hook for explicit loading of replies
  const getRepliesForComment = (commentId: string, contentGroupComments: CommentWithContentContext[]) => {
    return contentGroupComments.filter(comment => comment.parentCommentId === commentId);
  };
  
  // We use getRepliesForComment instead for specific content items
  // Helper to find all replies for a comment across all content items
  // const getAllRepliesForComment = (commentId: string) => {
  //   return contentItems.flatMap(item => 
  //     item.comments.filter(comment => comment.parentCommentId === commentId)
  //   );
  // };
  
  return (
    <div className={`${compact ? '' : ''} ${className}`} data-testid="comment-thread">
      {/* Comment Count and Filters */}
      {!compact && (
        <div className="">
          {/* Filters - only show if filter options provided */}
          {filterOptions.length > 0 && onFilter && (
            <CommentFilters
              options={filterOptions}
              activeFilter={activeFilter}
              onFilterChange={(filterId) => {
                console.log("EnhancedContextualCommentThread received filter change:", filterId);
                onFilter(filterId);
              }}
              className=""
            />
          )}
          
          {/* Title */}
          <div>
            <h3 className="text-base font-syne font-bold text-gray-800">
              {getTitle()}
            </h3>
          </div>
        </div>
      )}
      
      {/* New comment form - only show for 'all' or 'direct' filters */}
      {/* For contentCard context, form is moved below comments with a CTA toggle */}
      {pageContext !== 'contentCard' && (activeFilter === 'all' || activeFilter === 'direct') ? (
        <NewCommentForm
          onSubmit={handleSubmitComment}
          placeholder={commentPlaceholder ||
            (entityName ? `Share your thoughts about ${entityName}...` :
            (compact ? "Add your contribution..." : "Share your perspective..."))}
        />
      ) : pageContext !== 'contentCard' ? (
        // For album/track filters, only show the message if there are no comments
        contentItems.length === 0 && !isLoading ? (
          <div className={`${compact ? 'py-4 text-sm' : 'py-8 text-base'} bg-[#faf9f7]/90 backdrop-blur-sm rounded-2xl mb-6 text-center border border-gray-100/50`}>
            <p className="text-gray-500 font-light px-6">
              {entityName ?
                `No ${activeFilter === 'album' ? 'album' : 'track'} comments yet. Browse ${entityName}'s discography to share what moves you.` :
                `No ${activeFilter === 'album' ? 'album' : 'track'} comments yet. Browse the discography to share what moves you.`}
            </p>
          </div>
        ) : null
      ) : null}
      
      {/* Loading state */}
      {isLoading && contentItems.length === 0 && (
        <div className="animate-pulse space-y-4 py-4">
          {[...Array(compact ? 1 : 3)].map((_, i) => (
            <div key={i} className="bg-[#faf9f7]/90 backdrop-blur-sm rounded-2xl p-5 shadow-sm border border-gray-100/50">
              <div className="flex space-x-3">
                <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Comments list */}
      <div className="space-y-4">
        {/* Show empty state for contributions */}
        {contentItems.length === 0 && !isLoading && (activeFilter === 'all' || activeFilter === 'direct') ? (
          <>
            <div className={`text-center ${compact ? 'py-2 text-sm' : 'py-4 text-base'} `}>
              <p className="text-gray-500 font-light italic">
                {compact ? "No contributions yet." : "No contributions yet."}
              </p>
            </div>
            {/* For contentCard with no comments, show the form directly */}
            {pageContext === 'contentCard' && (
              <div className="mt-4">
                <NewCommentForm
                  onSubmit={handleSubmitComment}
                  placeholder={entityName ? `Share your thoughts about ${entityName}...` : "Share your perspective..."}
                />
              </div>
            )}
          </>
        ) : null}
        
        {/* Content items exist but no comments - show form directly for contentCard */}
        {contentItems.length > 0 && getAllCommentsChronologically.length === 0 && pageContext === 'contentCard' && (
          <div className="mt-2">
            <NewCommentForm
              onSubmit={handleSubmitComment}
              placeholder={entityName ? `Share your thoughts about ${entityName}...` : "Share your perspective..."}
            />
          </div>
        )}

        {contentItems.length > 0 && getAllCommentsChronologically.length > 0 && (
          <>
            {/* Chronological comments across all content items */}
            <div className="space-y-4">
              {getAllCommentsChronologically.map(comment => {
                // Find the content item this comment belongs to
                const contentItem = contentItems.find(item => item.contentItem.id === comment.contentContext.contentId);
                if (!contentItem) return null; // Skip if content item not found (shouldn't happen)

                // Find replies for this comment
                const replies = getRepliesForComment(comment.commentId, contentItem.comments);
                // Use the expandedComments state to determine if replies should be expanded
                const isRepliesExpanded = expandedComments[comment.commentId] ?? true; // Default to expanded
                // Only show left border if there are no replies (avoids double border with nested content)
                const hasReplies = replies.length > 0;

                return (
                  <div key={comment.commentId} className={hasReplies ? "py-1" : "border-l border-coral-purple/70 pl-4 py-1"}>
                    {/* Main Comment */}
                    <CommentCard
                      comment={comment}
                      onLike={(commentId, isLiked) => onLike(commentId, isLiked, comment.contentContext.contentId)}
                      onEdit={onEdit ? (commentId, text) => onEdit(commentId, text, comment.contentContext.contentId) : undefined}
                      onDelete={onDelete ? (commentId) => onDelete(commentId, comment.contentContext.contentId) : undefined}
                      onStartAddingReply={handleStartAddingReply}
                      currentUserId={currentUser?.userId}
                      showContentContext={shouldShowContentContext(comment)}
                      viewingContext={
                        pageContext ? {
                          type: pageContext,
                          entityId: currentEntityId || currentAlbumId,
                          entityTitle: entityName
                        } : undefined
                      }
                      textFirstLayout={pageContext === 'contentCard'}
                      showAuthorAttribution={pageContext === 'contentCard'}
                    />

                    {/* Use ReplyThread component for showing replies */}
                    <ReplyThread
                        comment={comment}
                        contentId={comment.contentContext.contentId}
                        initialReplies={replies}
                        onReply={handleReply}
                        onLike={onLike}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onCancelAddingReply={handleCancelAddingReply}
                        currentUserId={currentUser?.userId}
                        isExpanded={isRepliesExpanded}
                        isAddingReply={addingReplyToCommentId === comment.commentId}
                        onToggleExpand={() => toggleReplies(comment.commentId)}
                        showContentContext={shouldShowContentContext(comment)}
                      />
                  </div>
                );
              })}
            </div>

            {/* For contentCard context: Show "Add a different perspective" CTA and collapsible form */}
            {pageContext === 'contentCard' && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                {!showNewThreadForm ? (
                  <button
                    onClick={() => setShowNewThreadForm(true)}
                    className="text-sm text-gray-500 hover:text-coral-pink transition-colors duration-200 font-light"
                  >
                    Add a different perspective{entityName ? ` on ${entityName}` : ''}
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 font-light">
                        Share a different perspective{entityName ? ` on ${entityName}` : ''}
                      </span>
                      <button
                        onClick={() => setShowNewThreadForm(false)}
                        className="text-xs text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      >
                        Cancel
                      </button>
                    </div>
                    <NewCommentForm
                      onSubmit={async (text, mentions) => {
                        await handleSubmitComment(text, mentions);
                        setShowNewThreadForm(false);
                      }}
                      placeholder={entityName ? `Share your perspective on ${entityName}...` : "Share your perspective..."}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CommentFeed;